// The archive. Every image the user owns, as one contact sheet (IMPLEMENTATION_PLAN.md §11.1).
//
// Paging is cursor-only: `next_cursor` is an opaque server token (§7) and is passed straight back.
// Nothing here ever constructs one.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Lightbox } from "../components/Lightbox";
import type { CSSProperties, ReactElement } from "react";
import { Link } from "react-router-dom";
import { Search, Download, Trash2 } from "lucide-react";
import { api, formatBytes, formatDate, formatRelative } from "../api";
import type { ImageAsset, ImageOrigin } from "../api";
import {
  AsyncButton,
  ConfirmModal,
  EmptyState,
  ErrorNote,
  Modal,
  Skeleton,
  describeError,
} from "../components/ui";
import { LIBRARY_COLUMN_CHOICES, usePreferences } from "../hooks/usePreferences";

type Dialog = { kind: "detail"; image: ImageAsset } | { kind: "delete"; image: ImageAsset } | null;

const ORIGINS: { value: ImageOrigin | ""; label: string }[] = [
  { value: "", label: "All images" },
  { value: "generated", label: "Generated" },
  { value: "uploaded", label: "Uploaded" },
];

/**
 * Above this the density preference is honoured; below it the stylesheet's own rules run the grid
 * — two columns on a phone, four on a tablet — and an inline value would only fight them (§21.1).
 */
const WIDE_QUERY = "(min-width: 1025px)";

const SKELETON_CELLS = [0, 1, 2, 3, 4, 5, 6, 7];

export function LibraryPage(): ReactElement {
  const [preferences, setPreferences] = usePreferences();
  const wide = useWideScreen();

  const [images, setImages] = useState<ImageAsset[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [modelId, setModelId] = useState("");
  const [origin, setOrigin] = useState<ImageOrigin | "">("");

  const [dialog, setDialog] = useState<Dialog>(null);
  const [deleteError, setDeleteError] = useState<unknown>(null);

  // Typing in the search box should not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setError(null);
      const page = await api.listImages(
        { q: query || undefined, model_id: modelId || undefined, origin: origin || undefined },
        signal,
      );
      setImages(page.items);
      setNextCursor(page.next_cursor);
    },
    [query, modelId, origin],
  );

  useEffect(() => {
    const controller = new AbortController();
    setImages(null);
    load(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setError(cause);
      setImages([]);
    });
    return () => controller.abort();
  }, [load]);

  async function loadMore(): Promise<void> {
    if (!nextCursor) return;
    try {
      const page = await api.listImages({
        cursor: nextCursor,
        q: query || undefined,
        model_id: modelId || undefined,
        origin: origin || undefined,
      });
      setImages((current) => [...(current ?? []), ...page.items]);
      setNextCursor(page.next_cursor);
    } catch (cause) {
      setError(cause);
    }
  }

  // Built from what has actually been loaded, so the filter only ever offers a model that has
  // images behind it. It widens as more pages come in, which is the honest behaviour here.
  const modelOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const image of images ?? []) {
      if (image.model_id) seen.add(image.model_id);
    }
    return Array.from(seen).sort();
  }, [images]);

  async function confirmDelete(image: ImageAsset): Promise<void> {
    setDeleteError(null);
    try {
      await api.deleteImage(image.id);
      setImages((current) => (current ?? []).filter((item) => item.id !== image.id));
      setDialog(null);
    } catch (cause) {
      // A 409 means the image is an input to a generation and the row is restricted (§7). The
      // server's message already says what to delete first, so show it verbatim.
      setDeleteError(cause);
      setDialog({ kind: "detail", image });
    }
  }

  const failure = error ? describeError(error) : null;
  const filtered = query !== "" || modelId !== "" || origin !== "";

  // The one computed value on this page: how many columns the sheet is asked for.
  const sheetStyle: CSSProperties | undefined = wide
    ? { gridTemplateColumns: `repeat(${preferences.library_columns}, minmax(0, 1fr))` }
    : undefined;

  return (
    <div className="page">
      <div className="page-inner">
        <div className="page-header">
          <div>
            <h1 className="page-title">Library</h1>
            <p className="lede">Every frame you have made or uploaded, newest first.</p>
          </div>
        </div>

        <div className="toolbar">
          <div className="search">
            <Search size={15} aria-hidden />
            <input
              className="input"
              type="search"
              placeholder="Search prompts"
              aria-label="Search prompts"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          {/* .select fills its container by default, which in a wrapping toolbar would put every
              filter on a line of its own. Sized to its content instead, and never past the edge. */}
          <select
            className="select"
            style={{ width: "auto", maxWidth: "100%" }}
            aria-label="Filter by model"
            value={modelId}
            onChange={(event) => setModelId(event.target.value)}
          >
            <option value="">All models</option>
            {modelOptions.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>

          <select
            className="select"
            style={{ width: "auto", maxWidth: "100%" }}
            aria-label="Filter by origin"
            value={origin}
            onChange={(event) => setOrigin(event.target.value as ImageOrigin | "")}
          >
            {ORIGINS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Offered only where it does something. Narrower than this the grid is fixed at two or
              four columns, and a control with no effect is worse than no control. */}
          {wide && (
            <div className="segmented" role="group" aria-label="Contact sheet density">
              {LIBRARY_COLUMN_CHOICES.map((columns) => (
                <button
                  key={columns}
                  type="button"
                  className={preferences.library_columns === columns ? "is-selected" : undefined}
                  aria-pressed={preferences.library_columns === columns}
                  onClick={() => setPreferences({ library_columns: columns })}
                >
                  {columns}
                </button>
              ))}
            </div>
          )}
        </div>

        {failure && <ErrorNote title={failure.title} detail={failure.detail} />}

        {images === null && (
          <div className="contact-sheet" style={sheetStyle}>
            {SKELETON_CELLS.map((index) => (
              <div className="sheet-cell" key={index}>
                <Skeleton height={180} />
                <Skeleton height={12} width="80%" />
              </div>
            ))}
          </div>
        )}

        {images !== null && images.length === 0 && !failure && (
          <EmptyState
            title={filtered ? "Nothing matches those filters." : "No frames yet."}
            hint={
              filtered
                ? "Clear the filters to see everything."
                : "Images you generate or upload land here."
            }
            action={
              <Link className="btn primary" to="/">
                Open the studio
              </Link>
            }
          />
        )}

        {images !== null && images.length > 0 && (
          <>
            <div className="contact-sheet" style={sheetStyle}>
              {images.map((image) => (
                <button
                  type="button"
                  className="sheet-cell"
                  key={image.id}
                  onClick={() => {
                    setDeleteError(null);
                    setDialog({ kind: "detail", image });
                  }}
                >
                  <img
                    className="sheet-image"
                    src={image.thumb_url ?? image.content_url}
                    alt={image.prompt ?? "Untitled frame"}
                    loading="lazy"
                  />
                  <span className="sheet-caption">{image.prompt ?? "Uploaded image"}</span>
                  <span className="sheet-foot">
                    <span className="stamp truncate">{image.model_id ?? image.origin}</span>
                    <span className="stamp">{formatRelative(image.created_at)}</span>
                  </span>
                </button>
              ))}
            </div>

            {nextCursor && (
              <div
                className="row"
                style={{ justifyContent: "center", marginTop: "var(--space-5)" }}
              >
                <AsyncButton onClick={loadMore}>Load more</AsyncButton>
              </div>
            )}
          </>
        )}
      </div>

      {dialog?.kind === "detail" && (
        <ImageDetail
          image={dialog.image}
          error={deleteError}
          onDelete={() => setDialog({ kind: "delete", image: dialog.image })}
          onClose={() => {
            setDeleteError(null);
            setDialog(null);
          }}
        />
      )}

      {dialog?.kind === "delete" && (
        <ConfirmModal
          danger
          title="Delete this image?"
          body={
            <p>
              The file and its thumbnail are removed for good. Frames that used it as an input keep
              their record of it.
            </p>
          }
          confirmLabel="Delete image"
          onConfirm={() => confirmDelete(dialog.image)}
          onCancel={() => setDialog({ kind: "detail", image: dialog.image })}
        />
      )}
    </div>
  );
}

interface ImageDetailProps {
  image: ImageAsset;
  error: unknown;
  onDelete: () => void;
  onClose: () => void;
}

function ImageDetail({ image, error, onDelete, onClose }: ImageDetailProps): ReactElement {
  const failure = error ? describeError(error) : null;
  const [zoomed, setZoomed] = useState(false);

  return (
    <Modal
      wide
      title={image.model_id ?? "Uploaded image"}
      onClose={onClose}
      footer={
        <>
          <a className="btn" href={image.content_url} download>
            <Download size={14} aria-hidden />
            Download
          </a>
          <button type="button" className="btn danger" onClick={onDelete}>
            <Trash2 size={14} aria-hidden />
            Delete
          </button>
        </>
      }
    >
      {/* Whatever the server said, said again word for word: a 409 already names the frame that
          holds this image, and paraphrasing it would lose that. */}
      {failure && <ErrorNote title={failure.title} detail={failure.detail} />}

      <button
        type="button"
        className="print is-static is-zoomable"
        aria-label="View full screen"
        onClick={() => setZoomed(true)}
      >
        <img src={image.content_url} alt={image.prompt ?? "Full-size frame"} />
      </button>

      {zoomed && <Lightbox image={image} onClose={() => setZoomed(false)} />}

      {image.prompt && <p className="prose">{image.prompt}</p>}

      <div className="table-scroll">
        <table className="table">
          <tbody>
            <Row label="Origin" value={image.origin} />
            {image.model_id && <Row label="Model" value={image.model_id} />}
            <Row label="Size" value={`${image.width} × ${image.height}`} />
            <Row label="Type" value={image.content_type} />
            <Row label="On disk" value={formatBytes(image.byte_size)} />
            <Row label="Created" value={formatDate(image.created_at)} />
            {image.board_id && (
              <tr>
                <th scope="row">Board</th>
                <td>
                  <Link to={`/b/${image.board_id}`} onClick={onClose}>
                    Open the board
                  </Link>
                </td>
              </tr>
            )}
            <Row label="Id" value={image.id} />
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <tr>
      <th scope="row">{label}</th>
      <td className="stamp-value">{value}</td>
    </tr>
  );
}

/** Whether the contact sheet is wide enough for the density preference to mean anything. */
function useWideScreen(): boolean {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE_QUERY).matches);

  useEffect(() => {
    const query = window.matchMedia(WIDE_QUERY);
    const onChange = (event: MediaQueryListEvent) => setWide(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return wide;
}
